import React, { useState, useEffect } from "react";
import {
  Layout, Table, Button, Tag, message, Card, Row, Col, Select, Input, Dropdown, Modal, Space
} from "antd";
import { useNavigate } from "react-router-dom";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import {
  EyeOutlined, AuditOutlined, SearchOutlined, FilterOutlined, ClearOutlined, PlusOutlined, PrinterOutlined, EditOutlined, DeleteOutlined, MoreOutlined
} from "@ant-design/icons";
import api from "../../../services/axiosInstance";
import moment from "moment";
import "../../../styles/fluidDesign.css";
import AppBreadcrumb from "../../../components/common/AppBreadcrumb";
import { getAuthItem } from "../../../services/authService";
import GRNPreviewModal from "../../../components/common/GRNPreviewModal";
import { getStatusColor } from "../../../utils/statusHelper";

const { Content } = Layout;
const { Option } = Select;

const ManageGRN = () => {
  const navigate = useNavigate();
  const [grns, setGrns] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modal State
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewGrnId, setPreviewGrnId] = useState(null);

  // Filters (unapplied)
  const [supplierFilter, setSupplierFilter] = useState(undefined);
  const [statusFilter, setStatusFilter] = useState(undefined);
  const [searchRefFilter, setSearchRefFilter] = useState("");

  // Applied filters state
  const [appliedSupplier, setAppliedSupplier] = useState(undefined);
  const [appliedStatus, setAppliedStatus] = useState(undefined);
  const [appliedSearchRef, setAppliedSearchRef] = useState("");

  const isDisabled = (permName) => {
    // Current system semantics: 'false' = enabled, 'true' = disabled, missing = denied
    const value = getAuthItem(`USERPERMISSION_${permName}`);
    if (value === null) return true;
    return value !== 'false';
  };

  const handleDeleteGRN = async (grnId) => {
    setLoading(true);
    try {
      const res = await api.post("/inventory/grn/delete.php", { grn_id: grnId });
      if (res.data && res.data.status === "success") {
        message.success(res.data.message || "GRN deleted successfully.");
        loadData();
      } else {
        message.error(res.data.message || "Failed to delete GRN.");
      }
    } catch (error) {
      message.error("Error deleting GRN.");
    } finally {
      setLoading(false);
    }
  };

  // Load GRN list & filters info
  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get("/inventory/grn/list.php");
      if (res.data && res.data.status === "success") {
        setGrns(res.data.dataset);
      }

      const resSups = await api.get("/masterdropdown/listof_activesuppliers.php");
      if (resSups.data && resSups.data.status === "success") {
        setSuppliers(resSups.data.dataset);
      }
    } catch (error) {
      message.error("Error loading master data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleApplyFilters = () => {
    setAppliedSupplier(supplierFilter);
    setAppliedStatus(statusFilter);
    setAppliedSearchRef(searchRefFilter);
  };

  const handleClearFilters = () => {
    setSupplierFilter(undefined);
    setStatusFilter(undefined);
    setSearchRefFilter("");
    setAppliedSupplier(undefined);
    setAppliedStatus(undefined);
    setAppliedSearchRef("");
  };

  const getFilteredGRNs = () => {
    return grns.filter(grn => {
      const matchSupplier = !appliedSupplier || Number(grn.sup_id) === Number(appliedSupplier);
      const matchStatus = !appliedStatus || grn.grn_status === appliedStatus;
      const matchRef = !appliedSearchRef || grn.grn_ref.toLowerCase().includes(appliedSearchRef.toLowerCase());
      return matchSupplier && matchStatus && matchRef;
    });
  };

  // Render Status Tag
  const renderStatusTag = (status) => {
    return <Tag color={getStatusColor(status)} style={{ fontWeight: "600" }}>{status}</Tag>;
  };

  const columns = [
    {
      title: "GRN Reference",
      dataIndex: "grn_ref",
      key: "grn_ref",
      render: (text, record) => (
        <span style={{ fontWeight: "600", color: "var(--fluid-primary)", cursor: "pointer" }} onClick={() => navigate(`/grn/${record.grn_id}/view`)} className="clickable-text">
          {text}
        </span>
      )
    },
    {
      title: "PR Associated",
      dataIndex: "po_ref",
      key: "po_ref",
      render: (text) => text ? <Tag color="blue">{text}</Tag> : "-"
    },
    {
      title: "Supplier Ref",
      dataIndex: "sup_ref",
      key: "sup_ref",
      render: (text) => text ? <Tag color="cyan">{text}</Tag> : "-"
    },
    {
      title: "Supplier",
      dataIndex: "supplier_name",
      key: "supplier_name"
    },
    {
      title: "Received Date",
      dataIndex: "grn_date",
      key: "grn_date",
      render: (date) => moment(date).format("YYYY-MM-DD")
    },
    {
      title: "Grand Total Cost",
      dataIndex: "grn_final_amount",
      key: "grn_final_amount",
      align: "right",
      render: (val) => <strong>LKR {Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
    },
    {
      title: "Status",
      dataIndex: "grn_status",
      key: "grn_status",
      render: (status) => renderStatusTag(status)
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: 80,
      render: (_, record) => {
        const canEdit = record.grn_status === "Draft" && !isDisabled('grn_create');
        const canDelete = (record.grn_status === "Draft" || record.grn_status === "Pending Approval") && !isDisabled('grn_create');
        
        const items = [
          {
            key: "print",
            label: "Print",
            icon: <PrinterOutlined />,
            onClick: () => {
              setPreviewGrnId(record.grn_id);
              setPreviewModalVisible(true);
            }
          },
          {
            key: "view",
            label: "View Details",
            icon: <EyeOutlined />,
            onClick: () => navigate(`/grn/${record.grn_id}/view`)
          }
        ];

        if (canEdit) {
          items.push({
            key: "edit",
            label: "Edit Draft GRN",
            icon: <EditOutlined />,
            onClick: () => navigate(`/grn/receive/${record.pr_id || 0}?grnId=${record.grn_id}`)
          });
        }

        if (canDelete) {
          items.push({
            key: "delete",
            label: "Delete GRN",
            icon: <DeleteOutlined />,
            danger: true,
            onClick: () => {
              Modal.confirm({
                title: "Delete GRN",
                content: "Are you sure you want to delete this GRN?",
                okText: "Yes, Delete",
                okType: "danger",
                cancelText: "No",
                onOk: () => handleDeleteGRN(record.grn_id)
              });
            }
          });
        }

        return (
          <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
            <Button type="text" icon={<MoreOutlined />} className="fluid-dots-btn" />
          </Dropdown>
        );
      }
    }
  ];

  return (
    <Layout className="fluid-page">
      <Headerdiv />

      <div className="fluid-page-header">
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>
        <Row align="bottom" justify="space-between">
          <Col>
            <AppBreadcrumb items={[
              { label: 'Inventory', link: '/inventory-items' },
              { label: 'GRN List' },
            ]} />
            <h1 className="fluid-page-title">
              <AuditOutlined /> Goods Received Notes (GRNs)
            </h1>
          </Col>
          <Col xs={0} md={12} style={{ textAlign: 'right' }}>
            <Space>
              {!isDisabled('grn_create') && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => navigate("/grn/create")}
                  className="fluid-btn-visible"
                >
                  Create GRN
                </Button>
              )}
            </Space>
          </Col>
        </Row>
      </div>

      <Content className="fluid-content">
        <div className="fluid-card" style={{ marginBottom: "20px", padding: "20px" }}>
          <Row gutter={[16, 16]} align="bottom" className="fluid-filter-row">
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Supplier:</div>
              <Select
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) => {
                  const label = option?.label || option?.children;
                  if (Array.isArray(label)) {
                    return label.join('').toLowerCase().includes(input.toLowerCase());
                  }
                  return String(label || '').toLowerCase().includes(input.toLowerCase());
                }}
                placeholder="Choose Supplier"
                allowClear
                value={supplierFilter}
                onChange={setSupplierFilter}
                style={{ width: "100%" }}
                className="fluid-select"
              >
                {suppliers.map((sup) => (
                  <Option key={sup.value} value={Number(sup.value)}>
                    {sup.label}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Status:</div>
              <Select
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) => {
                  const label = option?.label || option?.children;
                  if (Array.isArray(label)) {
                    return label.join('').toLowerCase().includes(input.toLowerCase());
                  }
                  return String(label || '').toLowerCase().includes(input.toLowerCase());
                }}
                placeholder="Choose Status"
                allowClear
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: "100%" }}
                className="fluid-select"
              >
                <Option value="Pending Approval">Pending</Option>
                <Option value="Approved">Approved</Option>
                <Option value="Rejected">Rejected</Option>
              </Select>
            </Col>

            <Col xs={24} sm={12} md={12} lg={5}>
              <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Search Reference:</div>
              <Input
                placeholder="Search GRN Ref..."
                allowClear
                value={searchRefFilter}
                onChange={(e) => setSearchRefFilter(e.target.value)}
                prefix={<SearchOutlined />}
                onPressEnter={handleApplyFilters}
                className="fluid-input"
              />
            </Col>

            <Col xs={24} sm={12} md={12} lg={5}>
              <Row gutter={8} className="fluid-filter-actions">
                <Col span={12}>
                  <Button
                    type="primary"
                    icon={<FilterOutlined />}
                    onClick={handleApplyFilters}
                    className="fluid-btn-primary"
                    block
                  >
                    Filter
                  </Button>
                </Col>
                <Col span={12}>
                  <Button
                    icon={<ClearOutlined />}
                    onClick={handleClearFilters}
                    className="fluid-btn-secondary"
                    block
                  >
                    Clear
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>

        <Card className="fluid-card">
          <Table
            columns={columns}
            dataSource={getFilteredGRNs()}
            rowKey="grn_id"
            loading={loading}
            pagination={{ pageSize: 10 }}
            bordered
            size="middle"
          />
        </Card>

        {previewModalVisible && (
          <GRNPreviewModal
            grnId={previewGrnId}
            visible={previewModalVisible}
            onCancel={() => setPreviewModalVisible(false)}
          />
        )}
      </Content>
      <Footerdiv />
    </Layout>
  );
};

export default ManageGRN;
